import { useId, useState } from 'react';
import { EXPLORE_PRIVACY_POLICY_SUMMARY_URL } from '../../config/legalUrls';

const ONE_LINE =
  'Search shows web results. Don\u2019t type private info. Ask a grown-up if you need help.';

const EXPANDED_BODY_BEFORE_LINK =
  'Some results may link to websites we do not control. Do not type your full name, address, phone number, school name, password, or other private information. Search should be used with parent or teacher guidance. Read our ';

const PARENT_TEACHER_SETTINGS =
  'Parent / teacher: to turn off search on this device, use the side menu to open Settings (sign in and confirm if asked), then open the Learner tab and turn off Search on this device. Nothing here jumps straight to Settings so a grown-up can do those steps with you.';

const linkStyle = {
  color: 'var(--color-primary)',
  fontWeight: 700,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

/**
 * Compact privacy line + “Learn more” expander (same behavior on all viewports).
 * Shown under search inputs on Search and Home hero.
 * @param {'default' | 'search'} [variant] — `search`: left-bias + padding so the line clears the fixed search bar / profile on desktop when the rail is visible.
 */
function SearchInputPrivacyNotice({ id = 'search-privacy-notice', variant = 'default' }) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const learnMoreId = `${id}-learn-more`;

  return (
    <div
      id={id}
      className={`search-privacy-notice-root${variant === 'search' ? ' search-privacy-notice-root--search' : ''}`}
      style={{
        marginTop: 6,
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
      }}
    >
      <div
        className="search-privacy-notice-line"
        style={{
          margin: 0,
          fontSize: 12,
          lineHeight: 1.35,
          color: 'var(--color-text)',
          fontWeight: 500,
        }}
      >
        <span>{ONE_LINE}</span>{' '}
        <button
          type="button"
          id={learnMoreId}
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: 'inline',
            margin: 0,
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 'inherit',
            fontWeight: 700,
            color: 'var(--color-primary)',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            verticalAlign: 'baseline',
            whiteSpace: 'nowrap',
          }}
        >
          {expanded ? 'Learn less' : 'Learn more'}
        </button>
      </div>

      {expanded ? (
        <div
          id={detailsId}
          className="search-privacy-notice-expanded"
          role="region"
          aria-labelledby={learnMoreId}
          style={{
            marginTop: 8,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-card)',
            boxShadow: '0 1px 3px var(--color-shadow)',
            fontSize: 13,
            lineHeight: 1.45,
            color: 'var(--color-text)',
          }}
        >
          {EXPANDED_BODY_BEFORE_LINK}
          <a
            href={EXPLORE_PRIVACY_POLICY_SUMMARY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            Privacy Policy
          </a>
          .
          <p
            style={{
              margin: '14px 0 0',
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--color-text)',
              opacity: 0.88,
            }}
          >
            {PARENT_TEACHER_SETTINGS}
          </p>
        </div>
      ) : null}

      <style>
        {`
          @media (min-width: 769px) {
            .search-privacy-notice-root .search-privacy-notice-line {
              text-align: center;
              max-width: 36rem;
              margin-left: auto;
              margin-right: auto;
              padding-left: 8px;
              padding-right: 8px;
            }
            .search-privacy-notice-root .search-privacy-notice-expanded {
              max-width: 36rem;
              margin-left: auto;
              margin-right: auto;
              text-align: left;
            }

            /* Search page: keep the line left-weighted so it is not clipped by the wide search row / profile */
            .search-privacy-notice-root--search .search-privacy-notice-line {
              text-align: left;
              margin-left: 0;
              margin-right: auto;
              max-width: min(42rem, 100%);
              padding-left: 0;
              padding-right: clamp(5.5rem, 14vw, 7.5rem);
              box-sizing: border-box;
            }
            .App[data-sidebar-rail="visible"] .search-privacy-notice-root--search .search-privacy-notice-line {
              padding-left: 0;
              margin-left: 0;
              max-width: min(42rem, calc(100% - 0.5rem));
            }
            .search-privacy-notice-root--search .search-privacy-notice-expanded {
              margin-left: 0;
              margin-right: auto;
              max-width: min(42rem, 100%);
              padding-right: clamp(1rem, 4vw, 1.5rem);
            }
            .App[data-sidebar-rail="visible"] .search-privacy-notice-root--search .search-privacy-notice-expanded {
              max-width: min(42rem, calc(100% - 0.5rem));
            }
          }
        `}
      </style>
    </div>
  );
}

export default SearchInputPrivacyNotice;
